import React, { useEffect, useMemo, useRef, useState } from "react";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { PAGE_LAYOUT } from "../../layout/pageLayout";
import { ReportBrandBlock } from "../report/ReportBrandBlock";
import { useMembership } from "../../context/MembershipContext";
import { useOrganization } from "../../context/OrganizationContext";
import { resolveOrgBranding } from "../../helpers/resolveOrgBranding";
import {
  removeOrganizationLogo,
  uploadOrganizationLogo,
  validateOrganizationLogoFile,
} from "../../helpers/orgAssetUpload";
import "../report/ReportBrandBlock.css";

export function AdminBrandingScreen({ onBack }) {
  const { settings, updateSettings, loading, error } = useOrganization();
  const { organizationId } = useMembership();
  const fileInputRef = useRef(null);

  const [pendingFile, setPendingFile] = useState(null);
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    return () => {
      if (pendingPreviewUrl) {
        URL.revokeObjectURL(pendingPreviewUrl);
      }
    };
  }, [pendingPreviewUrl]);

  const savedBranding = useMemo(
    () => resolveOrgBranding(settings),
    [settings],
  );

  const previewBranding = useMemo(() => {
    if (pendingPreviewUrl) {
      return { ...savedBranding, logoUrl: pendingPreviewUrl };
    }
    return savedBranding;
  }, [savedBranding, pendingPreviewUrl]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    setActionError(null);
    setSaved(false);

    if (!file) {
      return;
    }

    const validation = validateOrganizationLogoFile(file);
    if (!validation.valid) {
      setActionError(validation.error);
      event.target.value = "";
      return;
    }

    if (pendingPreviewUrl) {
      URL.revokeObjectURL(pendingPreviewUrl);
    }

    setPendingFile(file);
    setPendingPreviewUrl(URL.createObjectURL(file));
  };

  const handleUploadAndSave = async () => {
    if (!organizationId) {
      setActionError("Organization context is unavailable.");
      return;
    }

    if (!pendingFile) {
      setActionError("Choose a logo file before saving.");
      return;
    }

    setUploading(true);
    setActionError(null);

    try {
      const publicUrl = await uploadOrganizationLogo({
        organizationId,
        file: pendingFile,
      });

      await updateSettings({
        branding: { logo_url: publicUrl },
      });

      if (pendingPreviewUrl) {
        URL.revokeObjectURL(pendingPreviewUrl);
      }
      setPendingFile(null);
      setPendingPreviewUrl(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error("[AdminBranding] upload failed", err);
      setActionError(err.message || "Unable to upload logo.");
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!organizationId) {
      return;
    }

    setRemoving(true);
    setActionError(null);

    try {
      await removeOrganizationLogo({ organizationId });
      await updateSettings({
        branding: { logo_url: null },
      });

      if (pendingPreviewUrl) {
        URL.revokeObjectURL(pendingPreviewUrl);
      }
      setPendingFile(null);
      setPendingPreviewUrl(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error("[AdminBranding] remove failed", err);
      setActionError(err.message || "Unable to remove logo.");
    } finally {
      setRemoving(false);
    }
  };

  const hasSavedLogo = Boolean(savedBranding.logoUrl);
  const busy = uploading || removing;

  return (
    <CrewPilotModuleShell
      title="Branding"
      subtitle="Logo for customer-facing reports"
      sectionLabel="Company"
      onBack={onBack}
      layout={PAGE_LAYOUT.FORM}
    >
      <div className="adm-split">
      <div className="rb-card">
        <div className="section-heading">ORGANIZATION LOGO</div>

        {loading && (
          <p className="admin-placeholder-note" style={{ marginBottom: "12px" }}>
            Loading branding settings…
          </p>
        )}

        {error && (
          <p className="admin-placeholder-note" style={{ marginBottom: "12px" }}>
            Database sync unavailable. Changes are cached locally until online.
          </p>
        )}

        <p className="admin-placeholder-note">
          Company name and contact details are managed under Administration →
          Organization. This screen controls the logo shown on estimate,
          invoice, and payroll reports.
        </p>

        {hasSavedLogo && !pendingPreviewUrl && (
          <img
            src={savedBranding.logoUrl}
            alt="Current organization logo"
            className="admin-branding-logo-preview"
          />
        )}

        <label className="admin-field-label admin-branding-file-input adm-file">
          Upload logo (PNG, JPEG, or WebP — max 2 MB)
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="admin-text-input"
            disabled={busy}
            onChange={handleFileChange}
          />
        </label>

        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "12px" }}>
          <button
            type="button"
            className="kpi-back-btn adm-primary"
            onClick={handleUploadAndSave}
            disabled={busy || !pendingFile}
          >
            {uploading ? "Saving…" : "Save logo"}
          </button>

          {(hasSavedLogo || pendingPreviewUrl) && (
            <button
              type="button"
              className="kpi-back-btn adm-danger"
              onClick={handleRemoveLogo}
              disabled={busy}
            >
              {removing ? "Removing…" : "Remove logo"}
            </button>
          )}
        </div>

        {actionError && (
          <p className="admin-placeholder-note" style={{ marginTop: "12px" }}>
            {actionError}
          </p>
        )}

        {saved && (
          <p className="admin-save-confirmation" role="status">
            Branding updated.
          </p>
        )}
      </div>

      <div className="rb-card admin-branding-preview-card">
        <div className="section-heading">REPORT PREVIEW</div>
        <ReportBrandBlock
          branding={previewBranding}
          variant="preview"
          showDocTitle={false}
        />
        <p className="admin-branding-preview-note">
          This is how your reports will appear to customers.
        </p>
      </div>
      </div>
    </CrewPilotModuleShell>
  );
}
